<template>
  <div class="glass-panel p-6">
    <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-6">Cost Optimization Recommendations</h2>
    
    <div v-if="sortedRecommendations.length === 0" class="text-center py-12">
      <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-gray-500 dark:text-gray-400">No recommendations at this time</p>
    </div>
    
    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div 
        v-for="rec in sortedRecommendations" 
        :key="rec.id"
        class="p-4 border border-gray-200 dark:border-white/10 rounded-lg hover:shadow-md hover:border-purple-500 dark:hover:border-purple-500 transition-all"
      >
        <div class="flex justify-between items-start mb-2">
          <h3 class="font-semibold text-gray-900 dark:text-white">{{ rec.title }}</h3>
          <span :class="getPriorityClass(rec.priority)" class="text-xs px-2 py-1 rounded font-medium uppercase">
            {{ rec.priority }}
          </span>
        </div>
        
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">{{ rec.description }}</p>
        
        <div class="flex items-center gap-2 mb-3">
          <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          <span class="text-xs text-gray-500 dark:text-gray-400 capitalize">{{ rec.type.replace('_', ' ') }}</span>
        </div>
        
        <div class="flex justify-between items-center">
          <div class="text-green-600 dark:text-green-400 font-bold">
            Save {{ formatCurrency(rec.potentialSavings) }}/month
          </div>
          <button 
            @click="$emit('action', rec)"
            class="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-purple-500/50 transition-all"
          >
            {{ rec.action }}
          </button>
        </div>
      </div>
    </div>
    
    <div v-if="sortedRecommendations.length > 0" class="mt-6 p-4 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 rounded-lg">
      <div class="flex items-center gap-3">
        <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p class="font-semibold text-green-900 dark:text-green-100">Total Potential Savings</p>
          <p class="text-2xl font-bold text-green-600 dark:text-green-400">
            {{ formatCurrency(totalSavings) }}/month
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  recommendations: Array<{
    id: string
    type: 'idle_warehouse' | 'oversized' | 'long_query' | 'duplicate'
    title: string
    description: string
    potentialSavings: number
    action: string
    priority: 'high' | 'medium' | 'low'
  }>
}>()

const emit = defineEmits(['action'])

const { formatCurrency } = useCostFormatting()

const sortedRecommendations = computed(() => {
  const priorityOrder = { high: 3, medium: 2, low: 1 }
  return [...props.recommendations].sort((a, b) => {
    const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority]
    if (priorityDiff !== 0) return priorityDiff
    return b.potentialSavings - a.potentialSavings
  })
})

const totalSavings = computed(() => {
  return props.recommendations.reduce((sum, rec) => sum + rec.potentialSavings, 0)
})

const getPriorityClass = (priority: string): string => {
  const classes = {
    high: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300',
    medium: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
    low: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300'
  }
  return classes[priority as keyof typeof classes] || classes.low
}
</script>
